import React from 'react';
import { useQuery } from 'react-query';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Button,
  Divider,
} from '@mui/material';
import {
  Class,
  People,
  PersonAdd,
  TrendingUp,
  Assessment,
  School,
  Payment,
  Assignment,
  Percent,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { allocationsAPI, financeAPI, leadsAPI, teachersAPI } from '../../services/api';
import { formatLocalYMD, getLocalMonthStartYMD } from '../../utils/dateTimeUtils';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const today = formatLocalYMD(new Date());
  const firstDayOfMonth = getLocalMonthStartYMD(new Date());

  const { data: statsRes, isLoading: statsLoading } = useQuery(
    ['allocationsStats'],
    () => allocationsAPI.getStats(),
    { staleTime: 60000 }
  );
  const { data: newEnrollmentsRes, isLoading: newLoading } = useQuery(
    ['allocationsStatsNew', firstDayOfMonth, today],
    () => allocationsAPI.getStats({ startDate: firstDayOfMonth, endDate: today }),
    { staleTime: 60000 }
  );
  const { data: teachersRes, isLoading: teachersLoading } = useQuery(
    ['teachersActiveCount'],
    () => teachersAPI.getAll({ page: 1, limit: 1, status: 'active' }),
    { staleTime: 60000 }
  );
  const { data: overdueRes } = useQuery(
    ['financeOverdueCount'],
    () => financeAPI.getOverduePayments({ page: 1, limit: 1, scope: 'overdue' }),
    { staleTime: 60000 }
  );
  const { data: wonLeadsRes } = useQuery(
    ['leadsCountWon'],
    () => leadsAPI.getAll({ status: 'won', limit: 1, page: 1 }),
    { staleTime: 60000 }
  );
  const { data: otherLeadsRes } = useQuery(
    ['leadsCountOther'],
    () => leadsAPI.getAll({ limit: 1, page: 1 }),
    { staleTime: 60000 }
  );

  const stats = statsRes?.data?.stats ?? statsRes?.data ?? {};
  const newStats = newEnrollmentsRes?.data?.stats ?? newEnrollmentsRes?.data ?? {};
  const teachersTotal =
    teachersRes?.data?.pagination?.total ??
    (Array.isArray(teachersRes?.data?.data) ? teachersRes.data.data.length : 0);
  const overdueTotal = overdueRes?.data?.pagination?.total ?? 0;
  const wonLeadsTotal = wonLeadsRes?.data?.pagination?.total ?? 0;
  const otherLeadsTotal = otherLeadsRes?.data?.pagination?.total ?? 0;
  const totalLeads = wonLeadsTotal + otherLeadsTotal;
  const conversionRate = totalLeads > 0 ? ((wonLeadsTotal / totalLeads) * 100).toFixed(1) : '0';

  const dashboardData = {
    totalStudents: stats.totalStudents ?? 0,
    assignedStudents: stats.assignedStudents ?? 0,
    unassignedStudents: stats.unassignedStudents ?? 0,
    totalBatches: stats.totalBatches ?? 0,
    activeBatches: stats.activeBatches ?? 0,
    assignmentRate: stats.assignmentRate ?? 0,
    averageMatchScore: stats.averageMatchScore ?? 0,
  };
  const newEnrollments = newStats.totalStudents ?? 0;

  const isLoading = statsLoading || newLoading || teachersLoading;

  const StatCard = ({ title, value, icon, color, subtitle, onClick }) => (
    <Card sx={{ height: '100%', cursor: onClick ? 'pointer' : 'default' }} onClick={onClick}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ bgcolor: color, borderRadius: 1, p: 1, color: 'white' }}>{icon}</Box>
          <Box>
            <Typography variant="h4" fontWeight="bold">
              {value !== undefined && value !== null ? value : 'N/A'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          </Box>
        </Box>
        <Typography variant="subtitle1" sx={{ mt: 1 }}>
          {title}
        </Typography>
      </CardContent>
    </Card>
  );

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Admin Dashboard
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Live batches, teachers, new students this month, and allocation snapshot.
      </Typography>

      <Typography variant="h6" color="primary" sx={{ mb: 2 }}>
        Operational
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Live batches"
            value={dashboardData.activeBatches}
            subtitle={`${dashboardData.totalBatches} total (excl. inactive)`}
            icon={<Class />}
            color="#1976d2"
            onClick={() => navigate('/batches?status=live')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Active teachers"
            value={teachersTotal}
            subtitle="Active user accounts"
            icon={<PersonAdd />}
            color="#2e7d32"
            onClick={() => navigate('/teachers')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="New students"
            value={newEnrollments}
            subtitle={`Registered this month (since ${firstDayOfMonth})`}
            icon={<School />}
            color="#ed6c02"
            onClick={() =>
              navigate(
                `/students?created_from=${firstDayOfMonth}&created_to=${today}&isActive=true`
              )
            }
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Assignment rate"
            value={`${((dashboardData.assignmentRate || 0) * 100).toFixed(1)}%`}
            subtitle={`${dashboardData.assignedStudents} / ${dashboardData.totalStudents} students`}
            icon={<Assignment />}
            color="#9c27b0"
            onClick={() => navigate('/students?assignmentStatus=assigned&isActive=true')}
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      <Typography variant="h6" color="primary" sx={{ mb: 2 }}>
        Management overview
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total students"
            value={dashboardData.totalStudents}
            subtitle="Active · registered"
            icon={<People />}
            color="#1565c0"
            onClick={() => navigate('/students?isActive=true')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Unassigned"
            value={dashboardData.unassignedStudents}
            subtitle="Need allocation"
            icon={<Assessment />}
            color="#d32f2f"
            onClick={() => navigate('/students?assignmentStatus=unassigned&isActive=true')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Overdue payments"
            value={overdueTotal}
            subtitle="Past due invoices"
            icon={<Payment />}
            color="#f57c00"
            onClick={() => navigate('/finance?scope=overdue')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Match score"
            value={`${((dashboardData.averageMatchScore || 0) * 100).toFixed(1)}%`}
            subtitle="Average allocation quality"
            icon={<TrendingUp />}
            color="#7b1fa2"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Conversion rate"
            value={`${conversionRate}%`}
            subtitle={`Lifetime · ${wonLeadsTotal} won / ${totalLeads} leads`}
            icon={<Percent />}
            color="#00838f"
            onClick={() => navigate('/leads/analytics')}
          />
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Button variant="contained" onClick={() => navigate('/management')}>
          Sales & Enrollment
        </Button>
        <Button variant="outlined" onClick={() => navigate('/reports')}>
          Teacher Reports
        </Button>
        <Button variant="outlined" onClick={() => navigate('/finance')}>
          Finance
        </Button>
        <Button variant="outlined" onClick={() => navigate('/leads/analytics')}>
          Leads analytics
        </Button>
        <Button variant="outlined" onClick={() => navigate('/admin/users')}>
          Users
        </Button>
      </Box>
    </Box>
  );
};

export default AdminDashboard;
